import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import BookingDialog from './BookingDialog';
import ErrorBoundary from './ErrorBoundary';
import { Button } from './ui/button';
import { 
  Plus, ArrowLeft, Copy, XCircle, Users, MapPin, Clock, 
  Calendar as CalendarIcon, Trophy, ChevronRight, AlertCircle,
  CheckCircle, Phone, IndianRupee, Loader2, Filter, Pencil, UserPlus, X, Save,
  Lock, Unlock, ShieldCheck, ShieldAlert, BarChart3, Download, AlertTriangle, RefreshCw
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============== GAME LISTING ==============
const GameListing = ({ games, loading, onCreateNew, onViewGame, onViewReports, statusFilter, setStatusFilter, currentUser, facilities }) => {
  const [showExport, setShowExport] = useState(false);

  const getStatusColor = (status) => {
    switch(status) {
      case 'upcoming': return 'bg-blue-100 text-blue-700';
      case 'full': return 'bg-amber-100 text-amber-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'expired': return 'bg-gray-200 text-gray-600';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  return (
    <div className="p-4 lg:p-6 pb-28 lg:pb-6" data-testid="community-games-listing">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Community Games</h1>
          <p className="text-sm text-gray-500 mt-1">Create and manage pickup games</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={onViewReports} variant="outline" className="gap-2" data-testid="view-reports-btn">
            <BarChart3 className="h-4 w-4" /> Reports
          </Button>
          <Button onClick={() => setShowExport(true)} variant="outline" className="gap-2" data-testid="bulk-export-btn">
            <Download className="h-4 w-4" /> Export Data
          </Button>
          <Button onClick={onCreateNew} className="gap-2" data-testid="create-game-btn">
            <Plus className="h-4 w-4" /> Create Game
          </Button>
        </div>
      </div>

      {showExport && (
        <BulkExportModal
          currentUser={currentUser}
          facilities={facilities || []}
          onClose={() => setShowExport(false)}
        />
      )}
      {/* Status Filters */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {['all', 'upcoming', 'full', 'expired', 'cancelled', 'completed', 'converted'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              statusFilter === s 
                ? 'bg-gray-900 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`filter-${s}`}
          >
            {s === 'all' ? 'All Games' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : games.length === 0 ? (
        <div className="text-center py-20">
          <Trophy className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No community games yet</p>
          <p className="text-sm text-gray-400 mt-1">Create your first game to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {games.map(game => (
            <div
              key={game.id}
              onClick={() => onViewGame(game.id)}
              className="bg-white border border-gray-200 rounded-xl p-4 cursor-pointer hover:shadow-md transition-shadow"
              data-testid={`game-card-${game.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900 capitalize">
                      {game.sport}{game.format ? ` - ${game.format}` : ''}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${getStatusColor(game.status)}`}>
                      {game.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{game.venue_name}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <CalendarIcon className="h-3.5 w-3.5" />
                      {formatDate(game.date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {game.start_time} - {game.end_time}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-3.5 w-3.5" />
                      {game.players_filled}/{game.max_players}
                    </span>
                    <span className="flex items-center gap-1">
                      <IndianRupee className="h-3.5 w-3.5" />
                      {game.total_revenue?.toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== CREATE GAME FORM ==============
const CreateGameForm = ({ onBack, onCreated, facility }) => {
  const [step, setStep] = useState(1);
  const [sportsConfig, setSportsConfig] = useState(null);
  const [facilityData, setFacilityData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [slotCheckResult, setSlotCheckResult] = useState(null);
  const [checkingSlot, setCheckingSlot] = useState(false);
  const [blockSlotOnCreate, setBlockSlotOnCreate] = useState(true);

  const [formData, setFormData] = useState({
    venue_id: facility?.id || '',
    sport: '',
    format: '',
    date: '',
    start_time: '',
    end_time: '',
    court_id: '',
    court_name: '',
    skill_level: '',
    max_players: '',
    min_players: '',
    cost_per_player: '',
    description: '',
    location: facility?.address || '',
    auto_cancel_enabled: false,
    auto_cancel_deadline_hours: null,
  });

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const url = `${API_URL}/api/community-games/venue-sports-config/${facility?.id}`;
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setSportsConfig(data);
        }
        // Also load facility details for courts
        if (facility?.id) {
          const fData = await api.getFacility(facility.id);
          setFacilityData(fData);
          if (!formData.location && fData?.address) {
            setFormData(prev => ({ ...prev, location: fData.address }));
          }
        }
      } catch (e) {
        console.error('Failed to load sports config:', e);
      }
    };
    if (facility?.id) loadConfig();
  }, [facility?.id]);

  const availableSports = sportsConfig?.sports || [];
  const selectedSport = availableSports.find(s => s.name === formData.sport);
  const hasFormats = selectedSport?.formats?.length > 0;

  // Filter courts by selected sport AND format (strict mapping)
  const FORMAT_TO_COURT_TYPE = {
    '11v11': ['full'],
    '9v9': ['half'],
    '7v7': ['quarter'],
    '5v5': ['5aside'],
    'box': ['cricket-full', 'cricket-5aside'],
  };
  const allowedTypes = formData.format ? FORMAT_TO_COURT_TYPE[formData.format] : null;
  const availableCourts = facilityData?.courts?.filter(c => {
    if (!formData.sport) return false;
    // A court matches the selected sport if its primary `sport` matches,
    // OR if it lists the sport in `multi_sport` (e.g. shared football/cricket turf).
    const sportAliases = [formData.sport, formData.sport.replace('pickleball', 'pickle')];
    const courtSports = [c.sport, ...(Array.isArray(c.multi_sport) ? c.multi_sport : [])];
    const sportMatch = courtSports.some(cs => sportAliases.includes(cs));
    if (!sportMatch) return false;
    if (allowedTypes && !allowedTypes.includes(c.court_type)) return false;
    return c.status !== 'inactive';
  }) || [];

  // Generate 30-min interval time options
  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const label = `${h > 12 ? h - 12 : h === 0 ? 12 : h}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
      timeOptions.push({ value: val, label });
    }
  }

  // Duration validation helper
  const getDurationMins = (start, end) => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let startMins = sh * 60 + sm;
    let endMins = eh * 60 + em;
    // End time must be strictly after start time (no overnight for community games)
    return endMins - startMins;
  };

  const durationMins = getDurationMins(formData.start_time, formData.end_time);
  const endTimeInvalid = formData.start_time && formData.end_time && durationMins <= 0;
  const durationError = formData.start_time && formData.end_time && !endTimeInvalid && durationMins < 60;

  // Check slot availability when date/time/court changes on step 2
  const checkSlotAvailability = async () => {
    if (!formData.date || !formData.start_time || !formData.end_time || endTimeInvalid || durationError) {
      setSlotCheckResult(null);
      return;
    }
    setCheckingSlot(true);
    try {
      const params = new URLSearchParams({
        facility_id: formData.venue_id,
        court_id: formData.court_id || '',
        date: formData.date,
        start_time: formData.start_time,
        end_time: formData.end_time,
      });
      const result = await api.request(`/community-games/check-slot?${params}`);
      setSlotCheckResult(result);
    } catch {
      setSlotCheckResult(null);
    } finally {
      setCheckingSlot(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Final slot availability check before creating
      if (formData.court_id) {
        const params = new URLSearchParams({
          facility_id: formData.venue_id,
          court_id: formData.court_id,
          date: formData.date,
          start_time: formData.start_time,
          end_time: formData.end_time,
        });
        const slotCheck = await api.request(`/community-games/check-slot?${params}`);
        if (!slotCheck.available) {
          toast({ title: 'Slot Unavailable', description: slotCheck.reason, variant: 'destructive' });
          setLoading(false);
          return;
        }
      }

      const payload = {
        ...formData,
        max_players: parseInt(formData.max_players),
        min_players: parseInt(formData.min_players),
        cost_per_player: parseFloat(formData.cost_per_player),
        format: formData.format || null,
        skill_level: formData.skill_level || null,
        court_id: formData.court_id || null,
        court_name: formData.court_name || null,
        description: formData.description || null,
      };
      const result = await api.request('/community-games', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      // Block slot on calendar if checkbox is on and a court is selected
      if (blockSlotOnCreate && formData.court_id && result?.id) {
        try {
          await api.request(`/community-games/${result.id}/block-slot`, { method: 'POST' });
          toast({ title: 'Game Created & Slot Blocked', description: 'Community game created and slot blocked on calendar!' });
        } catch {
          toast({ title: 'Game Created', description: 'Game created but slot blocking failed. You can block it manually.' });
        }
      } else {
        toast({ title: 'Game Created', description: 'Community game created successfully!' });
      }

      onCreated(result);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch(step) {
      case 1: return formData.sport && (!hasFormats || formData.format);
      case 2: return formData.date && formData.start_time && formData.end_time && formData.skill_level && !durationError && !endTimeInvalid;
      case 3: return formData.max_players && formData.min_players && formData.cost_per_player;
      case 4: return true;
      default: return false;
    }
  };

  const stepLabels = ['Sport', 'Details', 'Players', 'Review'];

  return (
    <div className="p-4 lg:p-6 pb-28 lg:pb-6 max-w-2xl mx-auto" data-testid="create-game-form">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-bold">Create Game</h1>
      </div>

      {/* Steps Indicator */}
      <div className="flex items-center gap-1 mb-8">
        {stepLabels.map((label, i) => (
          <div key={i} className="flex-1">
            <div className={`h-1.5 rounded-full ${i + 1 <= step ? 'bg-gray-900' : 'bg-gray-200'}`} />
            <p className={`text-[10px] mt-1 text-center ${i + 1 <= step ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>{label}</p>
          </div>
        ))}
      </div>

      {/* Step 1: Sport */}
      {step === 1 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sport</label>
            <div className="grid grid-cols-2 gap-2">
              {availableSports.map(sport => (
                <button
                  key={sport.name}
                  onClick={() => setFormData(prev => ({ ...prev, sport: sport.name, format: '' }))}
                  className={`p-3 rounded-xl border-2 text-left capitalize font-medium transition-colors ${
                    formData.sport === sport.name 
                      ? 'border-gray-900 bg-gray-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`sport-${sport.name}`}
                >
                  {sport.name}
                </button>
              ))}
            </div>
          </div>
          {hasFormats && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Format</label>
              <div className="grid grid-cols-4 gap-2">
                {selectedSport.formats.map(fmt => (
                  <button
                    key={fmt}
                    onClick={() => setFormData(prev => ({ ...prev, format: fmt, court_id: '', court_name: '' }))}
                    className={`p-3 rounded-xl border-2 text-center font-medium transition-colors ${
                      formData.format === fmt 
                        ? 'border-gray-900 bg-gray-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`format-${fmt}`}
                  >
                    {fmt}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Game Details */}
      {step === 2 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={e => setFormData(prev => ({ ...prev, date: e.target.value }))}
              min={new Date().toISOString().split('T')[0]}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
              data-testid="game-date"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
              <select
                value={formData.start_time}
                onChange={e => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
                data-testid="game-start-time"
              >
                <option value="">Select</option>
                {timeOptions.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
              <select
                value={formData.end_time}
                onChange={e => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
                data-testid="game-end-time"
              >
                <option value="">Select</option>
                {timeOptions.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>
          {endTimeInvalid && (
            <p className="text-xs text-red-600 font-medium">End time must be after start time</p>
          )}
          {durationError && (
            <p className="text-xs text-red-600 font-medium">Minimum game duration is 1 hour</p>
          )}
          {formData.start_time && formData.end_time && !durationError && !endTimeInvalid && (
            <p className="text-xs text-gray-500">Duration: {Math.floor(durationMins / 60)}h {durationMins % 60 > 0 ? `${durationMins % 60}m` : ''}</p>
          )}
          {availableCourts.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Court / Ground</label>
              <select
                value={formData.court_id}
                onChange={e => {
                  const court = availableCourts.find(c => c.id === e.target.value);
                  setFormData(prev => ({ ...prev, court_id: e.target.value, court_name: court?.name || '' }));
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
                data-testid="game-court"
              >
                <option value="">Select court (optional)</option>
                {availableCourts.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          {/* Slot Availability Check */}
          {formData.date && formData.start_time && formData.end_time && !endTimeInvalid && !durationError && (
            <div className="space-y-2" data-testid="slot-check-section">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full gap-2"
                onClick={checkSlotAvailability}
                disabled={checkingSlot}
                data-testid="check-slot-btn"
              >
                {checkingSlot ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                Check Slot Availability
              </Button>
              {slotCheckResult && (
                <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
                  slotCheckResult.available 
                    ? 'bg-green-50 border border-green-200 text-green-700' 
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`} data-testid="slot-check-result">
                  {slotCheckResult.available 
                    ? <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" /> 
                    : <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  }
                  <span>{slotCheckResult.reason}</span>
                </div>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Skill Level</label>
            <div className="grid grid-cols-2 gap-2">
              {['beginner', 'intermediate', 'advanced', 'mixed'].map(level => (
                <button
                  key={level}
                  onClick={() => setFormData(prev => ({ ...prev, skill_level: level }))}
                  className={`p-2.5 rounded-lg border-2 text-sm capitalize font-medium transition-colors ${
                    formData.skill_level === level 
                      ? 'border-gray-900 bg-gray-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`skill-${level}`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Players & Pricing */}
      {step === 3 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Players</label>
            <input
              type="number"
              value={formData.max_players}
              onChange={e => setFormData(prev => ({ ...prev, max_players: e.target.value }))}
              placeholder="e.g., 14"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
              data-testid="max-players"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min Players (for validation)</label>
            <input
              type="number"
              value={formData.min_players}
              onChange={e => setFormData(prev => ({ ...prev, min_players: e.target.value }))}
              placeholder="e.g., 10"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
              data-testid="min-players"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cost per Player (INR)</label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="number"
                value={formData.cost_per_player}
                onChange={e => setFormData(prev => ({ ...prev, cost_per_player: e.target.value }))}
                placeholder="e.g., 300"
                className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2.5 text-sm"
                data-testid="cost-per-player"
              />
            </div>
          </div>

          {/* Auto-cancel Toggle */}
          <div className="border border-gray-200 rounded-xl p-4 space-y-3" data-testid="auto-cancel-section">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Auto-cancel</p>
                <p className="text-xs text-gray-500">Cancel if min players not reached</p>
              </div>
              <button
                onClick={() => setFormData(prev => ({ ...prev, auto_cancel_enabled: !prev.auto_cancel_enabled }))}
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${formData.auto_cancel_enabled ? 'bg-blue-500' : 'bg-gray-300'}`}
                data-testid="auto-cancel-toggle"
                type="button"
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${formData.auto_cancel_enabled ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
            {formData.auto_cancel_enabled && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Hours before game to auto-cancel</label>
                <input
                  type="number"
                  value={formData.auto_cancel_deadline_hours || ''}
                  onChange={e => setFormData(prev => ({ ...prev, auto_cancel_deadline_hours: e.target.value ? parseInt(e.target.value) : null }))}
                  placeholder="e.g., 2"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="auto-cancel-hours"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 4: Review & Additional */}
      {step === 4 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Any additional details about the game..."
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
              data-testid="game-description"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={e => setFormData(prev => ({ ...prev, location: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
              data-testid="game-location"
            />
          </div>

          {/* Review Summary */}
          <div className="mt-6 bg-gray-50 rounded-xl p-4 space-y-3">
            <h3 className="font-semibold text-sm">Review</h3>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <span className="text-gray-500">Sport</span>
              <span className="font-medium capitalize">{formData.sport} {formData.format}</span>
              <span className="text-gray-500">Date</span>
              <span className="font-medium">{formData.date}</span>
              <span className="text-gray-500">Time</span>
              <span className="font-medium">{formData.start_time} - {formData.end_time}</span>
              {formData.court_name && <>
                <span className="text-gray-500">Court</span>
                <span className="font-medium">{formData.court_name}</span>
              </>}
              {formData.skill_level && <>
                <span className="text-gray-500">Skill</span>
                <span className="font-medium capitalize">{formData.skill_level}</span>
              </>}
              <span className="text-gray-500">Players</span>
              <span className="font-medium">{formData.min_players} - {formData.max_players}</span>
              <span className="text-gray-500">Cost/Player</span>
              <span className="font-medium">&#8377;{formData.cost_per_player}</span>
              {formData.auto_cancel_enabled && <>
                <span className="text-gray-500">Auto-cancel</span>
                <span className="font-medium text-amber-600">{formData.auto_cancel_deadline_hours ? `${formData.auto_cancel_deadline_hours}h before` : 'Enabled'}</span>
              </>}
            </div>
          </div>

          {/* Block Slot Checkbox */}
          {formData.court_id && (
            <div className="border border-blue-200 bg-blue-50 rounded-xl p-4" data-testid="block-slot-section">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={blockSlotOnCreate}
                  onChange={e => setBlockSlotOnCreate(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  data-testid="block-slot-checkbox"
                />
                <div>
                  <p className="text-sm font-medium text-blue-900">Block this slot on the calendar</p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    Prevents regular bookings on <strong>{formData.court_name}</strong> from {formData.start_time} to {formData.end_time} on {formData.date}. You can unblock it later from the game details.
                  </p>
                </div>
              </label>
            </div>
          )}
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-3 mt-8">
        {step > 1 && (
          <Button variant="outline" onClick={() => setStep(s => s - 1)} className="flex-1">
            Back
          </Button>
        )}
        {step < 4 ? (
          <Button 
            onClick={() => setStep(s => s + 1)} 
            disabled={!canProceed()} 
            className="flex-1"
            data-testid="next-step-btn"
          >
            Next
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit} 
            disabled={loading} 
            className="flex-1"
            data-testid="create-game-submit"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Create Game
          </Button>
        )}
      </div>
    </div>
  );
};

// ============== GAME DETAIL VIEW ==============
const GameDetail = ({ gameId, onBack }) => {
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelPctChoice, setCancelPctChoice] = useState('100'); // '100' | '50' | '0' | 'custom'
  const [cancelCustomPct, setCancelCustomPct] = useState('');
  const [cancelLinkedBooking, setCancelLinkedBooking] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [newParticipant, setNewParticipant] = useState({ name: '', phone: '' });
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [blockingSlot, setBlockingSlot] = useState(false);
  const [showBookSlot, setShowBookSlot] = useState(false);
  const [facilityForBook, setFacilityForBook] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadGame = useCallback(async () => {
    try {
      const data = await api.request(`/community-games/${gameId}`);
      setGame(data);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => { loadGame(); }, [loadGame]);

  const handleCopyLink = () => {
    const link = `${window.location.origin}${game.public_link}`;
    navigator.clipboard.writeText(link);
    toast({ title: 'Copied!', description: 'Invite link copied to clipboard' });
  };

  // Opens the cancel confirmation dialog. The actual API call happens in
  // confirmCancel() after the admin picks a refund percentage (and, for
  // completed games, decides whether to also cancel the linked court booking).
  const handleCancel = () => {
    // Default refund % by game lifecycle:
    //  - upcoming / open / full → 100% (standard admin cancel of an upcoming game)
    //  - completed              → 0%   (safer default: game already happened; admin
    //                                   must consciously opt into refunding)
    setCancelPctChoice(game?.status === 'completed' ? '0' : '100');
    setCancelCustomPct('');
    // Only completed games have a linked court booking worth cancelling.
    const hasLinked = !!(game?.linked_booking_id || game?.converted_booking_id);
    setCancelLinkedBooking(game?.status === 'completed' && hasLinked);
    setShowCancelDialog(true);
  };

  const confirmCancel = async () => {
    // Resolve the chosen refund %. Custom accepts 0-100 integers only.
    let pct;
    if (cancelPctChoice === 'custom') {
      const n = parseInt(cancelCustomPct, 10);
      if (isNaN(n) || n < 0 || n > 100) {
        toast({ title: 'Invalid refund %', description: 'Enter a whole number between 0 and 100.', variant: 'destructive' });
        return;
      }
      pct = n;
    } else {
      pct = parseInt(cancelPctChoice, 10);
    }

    setCancelling(true);
    try {
      const res = await api.request(`/community-games/${gameId}/cancel`, {
        method: 'POST',
        body: JSON.stringify({
          refund_percentage: pct,
          cancel_linked_booking: cancelLinkedBooking,
        }),
      });
      // Surface refund outcomes faithfully: if any participant refund failed
      // (Razorpay rejected the payment id, gateway down, etc.) the admin needs
      // to know — a blanket "success" toast would hide the problem.
      const results = Array.isArray(res?.refund_results) ? res.refund_results : [];
      const failed = results.filter(r => r.status === 'failed');
      const linked = res?.linked_booking;
      let desc;
      if (pct > 0 && results.length > 0 && failed.length === results.length) {
        desc = `Game marked cancelled, but all ${results.length} participant refund(s) failed. Resolve from Finance → Stuck refunds.`;
      } else if (pct > 0 && failed.length > 0) {
        desc = `${pct}% refund initiated for ${results.length - failed.length}/${results.length} participants. ${failed.length} refund(s) failed — see Finance → Stuck refunds.`;
      } else if (pct > 0) {
        desc = `${pct}% refund initiated for ${results.length} participant(s).`;
      } else {
        desc = 'Game marked cancelled. No participant refund issued.';
      }
      if (linked?.status === 'cancelled') desc += ' Linked court booking cancelled.';
      else if (linked?.status === 'failed') desc += ` Linked booking cancel failed: ${linked.error}`;
      toast({
        title: failed.length > 0 && failed.length === results.length ? 'Partially Failed' : 'Game Cancelled',
        description: desc,
        variant: failed.length > 0 && failed.length === results.length ? 'destructive' : 'default',
      });
      setShowCancelDialog(false);
      loadGame();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setCancelling(false);
    }
  };

  const handleStartEdit = () => {
    setEditData({
      max_players: game.max_players,
      min_players: game.min_players,
      cost_per_player: game.cost_per_player,
      date: game.date,
      start_time: game.start_time,
      end_time: game.end_time,
      description: game.description || '',
      location: game.location || '',
    });
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editData,
        max_players: parseInt(editData.max_players),
        min_players: parseInt(editData.min_players),
        cost_per_player: parseFloat(editData.cost_per_player),
      };
      await api.request(`/community-games/${gameId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      toast({ title: 'Saved', description: 'Game updated successfully' });
      setEditing(false);
      loadGame();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddParticipant = async () => {
    if (!newParticipant.name.trim() || !newParticipant.phone.trim()) {
      toast({ title: 'Error', description: 'Name and phone are required', variant: 'destructive' });
      return;
    }
    setAddingParticipant(true);
    try {
      await api.request(`/community-games/${gameId}/add-participant`, {
        method: 'POST',
        body: JSON.stringify(newParticipant),
      });
      toast({ title: 'Added', description: `${newParticipant.name} added to the game` });
      setNewParticipant({ name: '', phone: '' });
      setShowAddParticipant(false);
      loadGame();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setAddingParticipant(false);
    }
  };

  const handleToggleSlotBlock = async () => {
    setBlockingSlot(true);
    try {
      const action = game.slot_blocked ? 'unblock-slot' : 'block-slot';
      await api.request(`/community-games/${gameId}/${action}`, { method: 'POST' });
      toast({ 
        title: game.slot_blocked ? 'Slot Unblocked' : 'Slot Blocked', 
        description: game.slot_blocked ? 'Calendar slot is now available for bookings' : 'Calendar slot is now blocked for this game'
      });
      loadGame();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setBlockingSlot(false);
    }
  };

  const handleBookSlot = async () => {
    const confirmed = window.confirm(
      'This will create a regular booking record for this slot ' +
      '(for admin tracking / payment collection).\n\n' +
      'The community game will REMAIN ACTIVE and visible on the public ' +
      '/join-a-game listing. Both records will coexist.\n\n' +
      'Continue?'
    );
    if (!confirmed) return;
    // Load facility (needed for BookingDialog pricing/court info) before opening
    try {
      const fData = await api.getFacility(game.venue_id);
      setFacilityForBook(fData);
      setShowBookSlot(true);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleRevertConverted = async () => {
    const confirmed = window.confirm(
      'Revert this game from "Converted" back to "Upcoming"?\n\n' +
      '- The community game will become visible on /join-a-game again.\n' +
      '- The linked regular booking will be cancelled (only if it has no paid amount).\n' +
      '- The calendar slot block will be restored.\n\n' +
      'Continue?'
    );
    if (!confirmed) return;
    try {
      const res = await api.request(`/community-games/${gameId}/revert-converted`, { method: 'POST' });
      toast({
        title: 'Reverted',
        description: res?.booking_action === 'cancelled'
          ? 'Game is back to Upcoming. Linked booking was cancelled.'
          : 'Game is back to Upcoming.',
      });
      loadGame();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  // Compute duration in 30-min slots from the game's start/end times
  const getGameDurationSlots = () => {
    if (!game?.start_time || !game?.end_time) return 2;
    const [sh, sm] = game.start_time.split(':').map(Number);
    const [eh, em] = game.end_time.split(':').map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins <= 0) mins += 24 * 60; // overnight
    return Math.max(1, Math.round(mins / 30));
  };

  const handleBookingCreated = async (newBooking) => {
    try {
      // Endpoint kept under the same path for compatibility, but now only
      // stamps a `linked_booking_id` on the community game — it does NOT
      // change the game status. The community game stays live on /join-a-game.
      await api.request(`/community-games/${gameId}/mark-converted`, {
        method: 'POST',
        body: JSON.stringify({ booking_id: newBooking?.id || null }),
      });
      toast({ title: 'Slot Booked', description: 'Regular booking created. The community game is still active and visible on /join-a-game.' });
      loadGame();
    } catch (err) {
      console.error('link-booking failed', err);
      toast({ title: 'Heads up', description: 'Booking saved but linking to the community game failed. Please refresh.', variant: 'destructive' });
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'upcoming': return 'bg-blue-100 text-blue-700';
      case 'full': return 'bg-amber-100 text-amber-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'expired': return 'bg-gray-200 text-gray-600';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPaymentBadge = (status) => {
    switch(status) {
      case 'paid': return 'bg-green-100 text-green-700';
      case 'refunded': return 'bg-orange-100 text-orange-700';
      case 'failed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
    </div>
  );

  if (!game) return (
    <div className="p-4 text-center py-20">
      <p className="text-gray-500">Game not found</p>
      <Button variant="outline" onClick={onBack} className="mt-4">Go Back</Button>
    </div>
  );

  const confirmedParticipants = (game.participants || []).filter(
    p => ['paid', 'manual'].includes(p.payment_status) && !p.is_waitlist
  );
  const waitlistParticipants = (game.participants || []).filter(
    p => p.is_waitlist && p.payment_status === 'paid' && p.waitlist_status === 'waitlist'
  ).sort((a, b) => (a.waitlist_position || 999) - (b.waitlist_position || 999));
  const otherParticipants = (game.participants || []).filter(
    p => !confirmedParticipants.includes(p) && !waitlistParticipants.includes(p)
  );
  // `isActive` governs mid-game admin actions (add/remove participants, promote
  // waitlist, sync pending payments). Those stay disabled for completed games.
  const isActive = !['cancelled', 'completed', 'expired'].includes(game.status);
  // `canCancel` governs ONLY the "Cancel Game" button. Admins can cancel a
  // COMPLETED game (via the refund-% dialog) in addition to any still-active
  // game. Expired/cancelled games cannot be cancelled.
  const canCancel = !['cancelled', 'expired'].includes(game.status);

  const handlePromoteWaitlist = async (participantId, participantName) => {
    if (!window.confirm(`Promote ${participantName} to confirmed? They will not be charged again.`)) return;
    try {
      await api.request(`/community-games/${gameId}/participants/${participantId}/promote-waitlist`, { method: 'POST' });
      toast({ title: 'Promoted', description: `${participantName} is now a confirmed player.` });
      loadGame();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  // Whether this participant can be removed via the admin DELETE endpoint.
  // Paid Razorpay participants must use the refund flow instead — they show no
  // Remove button at all (the customer-side Cancel button is the right path).
  const SAFE_REMOVE_STATUSES = new Set(['manual', 'pending', 'refunded', 'failed', 'cancelled']);
  const canRemoveParticipant = (p) => SAFE_REMOVE_STATUSES.has(p?.payment_status);

  const handleRemoveParticipant = async (participant) => {
    const label = participant.payment_status === 'manual'
      ? 'admin-added (offline)'
      : participant.payment_status;
    const confirmed = window.confirm(
      `Remove ${participant.name} (${label}) from this game?\n\n` +
      'No refund is issued (this is for offline/unpaid entries only). The slot ' +
      'will free up; if anyone is on the waitlist, they will be auto-promoted.\n\n' +
      'Continue?'
    );
    if (!confirmed) return;
    try {
      const res = await api.request(`/community-games/${gameId}/participants/${participant.id}`, {
        method: 'DELETE',
      });
      toast({
        title: 'Removed',
        description: res?.auto_promoted
          ? `${participant.name} removed. ${res.auto_promoted.name} auto-promoted from waitlist.`
          : `${participant.name} removed.`,
      });
      loadGame();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  // Reconcile a pending participant against Razorpay.
  // Triggered when admin sees money was captured in Razorpay but DB says pending.
  const handleSyncPayment = async (participant) => {
    if (!window.confirm(`Check Razorpay for payment status of ${participant.name}?\n\nIf Razorpay confirms the payment was captured, this participant will be marked paid.`)) return;
    setSyncingId(participant.id);
    try {
      const res = await api.request(`/community-games/${gameId}/participants/${participant.id}/sync-payment`, { method: 'POST' });
      if (res.status === 'synced') {
        toast({
          title: 'Payment confirmed',
          description: `${participant.name} marked as paid. Razorpay payment ID: ${res.razorpay_payment_id}`,
        });
      } else if (res.status === 'already_paid') {
        toast({ title: 'Already paid', description: 'This participant was already marked paid.' });
      } else if (res.status === 'refunded_full') {
        toast({
          title: 'Game was full — refund issued',
          description: 'The game became full before this payment could be applied. Customer has been refunded.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Razorpay says: not captured',
          description: res.message || 'No captured payment found for this order in Razorpay.',
          variant: 'destructive',
        });
      }
      loadGame();
    } catch (err) {
      toast({ title: 'Sync failed', description: err.message, variant: 'destructive' });
    } finally {
      setSyncingId(null);
    }
  };

  const handleBulkSync = async () => {
    if (!window.confirm('Sync ALL pending participants in this game against Razorpay?\n\nAny payments Razorpay confirms as captured will be flipped to paid.')) return;
    setBulkSyncing(true);
    try {
      const res = await api.request(`/community-games/${gameId}/sync-all-pending`, { method: 'POST' });
      const s = res.summary || {};
      const lines = [];
      if (s.synced) lines.push(`${s.synced} flipped to paid`);
      if (s.already_paid) lines.push(`${s.already_paid} already paid`);
      if (s.not_captured) lines.push(`${s.not_captured} not captured at Razorpay`);
      if (s.refunded_full) lines.push(`${s.refunded_full} refunded (game was full)`);
      if (s.error) lines.push(`${s.error} errors`);
      toast({
        title: res.scanned > 0 ? 'Sync complete' : 'Nothing to sync',
        description: res.scanned > 0 ? `Scanned ${res.scanned} pending — ${lines.join(', ') || 'no changes'}` : 'No pending participants with Razorpay orders.',
        variant: s.error > 0 ? 'destructive' : 'default',
      });
      loadGame();
    } catch (err) {
      toast({ title: 'Sync failed', description: err.message, variant: 'destructive' });
    } finally {
      setBulkSyncing(false);
    }
  };

  const handleExportGame = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/community-games/${gameId}/export`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Export failed');
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      const fileName = match ? match[1] : 'CommunityGame.xlsx';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Downloaded', description: fileName });
    } catch (err) {
      toast({ title: 'Export failed', description: err.message, variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4 lg:p-6 pb-28 lg:pb-6 max-w-3xl mx-auto" data-testid="game-detail-view">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold capitalize">
            {game.sport}{game.format ? ` - ${game.format}` : ''}
          </h1>
          <p className="text-sm text-gray-500">{game.venue_name}</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusColor(game.status)}`}>
          {game.status}
        </span>
      </div>

      {/* Legacy "Converted" safety-net banner — only visible for games that
          were converted under the old behavior. Lets admin restore them. */}
      {game.status === 'converted' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="converted-revert-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-sm text-amber-900">This game is in legacy "Converted" status</p>
            <p className="text-xs text-amber-800 mt-1">
              It's hidden from /join-a-game. Reverting will restore it to <strong>Upcoming</strong>,
              re-add the calendar block, and cancel the linked booking (only if no payment was collected).
            </p>
          </div>
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white"
            onClick={handleRevertConverted}
            data-testid="revert-converted-btn"
          >
            Revert to Upcoming
          </Button>
        </div>
      )}

      {/* Edit Mode */}
      {editing && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 space-y-3" data-testid="edit-game-panel">
          <h3 className="font-semibold text-sm text-amber-800">Edit Game</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Max Players</label>
              <input
                type="number"
                value={editData.max_players}
                onChange={e => setEditData(prev => ({ ...prev, max_players: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                data-testid="edit-max-players"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Min Players</label>
              <input
                type="number"
                value={editData.min_players}
                onChange={e => setEditData(prev => ({ ...prev, min_players: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                data-testid="edit-min-players"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Cost per Player (INR)</label>
            <input
              type="number"
              value={editData.cost_per_player}
              onChange={e => setEditData(prev => ({ ...prev, cost_per_player: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              data-testid="edit-cost"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Date</label>
              <input
                type="date"
                value={editData.date}
                onChange={e => setEditData(prev => ({ ...prev, date: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Start</label>
                <input
                  type="time"
                  value={editData.start_time}
                  onChange={e => setEditData(prev => ({ ...prev, start_time: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">End</label>
                <input
                  type="time"
                  value={editData.end_time}
                  onChange={e => setEditData(prev => ({ ...prev, end_time: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Description</label>
            <textarea
              value={editData.description}
              onChange={e => setEditData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <Button onClick={handleSaveEdit} disabled={saving} className="w-full gap-2" data-testid="save-edit-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Changes
          </Button>
        </div>
      )}

      {/* Top Section - Game Info Cards */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-white border rounded-xl p-3">
          <CalendarIcon className="h-4 w-4 text-gray-400 mb-1" />
          <p className="text-sm font-medium">{formatDate(game.date)}</p>
          <p className="text-xs text-gray-500">{game.start_time} - {game.end_time}</p>
        </div>
        <div className="bg-white border rounded-xl p-3">
          <Users className="h-4 w-4 text-gray-400 mb-1" />
          <p className="text-sm font-medium">{game.players_filled} / {game.max_players} Players</p>
          <p className="text-xs text-gray-500">Min: {game.min_players}</p>
        </div>
        <div className="bg-white border rounded-xl p-3">
          <IndianRupee className="h-4 w-4 text-gray-400 mb-1" />
          <p className="text-sm font-medium">&#8377;{(game.total_revenue || 0).toLocaleString('en-IN')}</p>
          <p className="text-xs text-gray-500">&#8377;{game.cost_per_player}/player</p>
        </div>
        <div className="bg-white border rounded-xl p-3">
          <MapPin className="h-4 w-4 text-gray-400 mb-1" />
          <p className="text-sm font-medium">{game.court_name || 'No court'}</p>
          <p className="text-xs text-gray-500 truncate">{game.location}</p>
        </div>
      </div>

      {game.skill_level && (
        <div className="bg-white border rounded-xl p-3 mb-6">
          <p className="text-xs text-gray-500">Skill Level</p>
          <p className="text-sm font-medium capitalize">{game.skill_level}</p>
        </div>
      )}

      {game.description && (
        <div className="bg-white border rounded-xl p-3 mb-6">
          <p className="text-xs text-gray-500 mb-1">Description</p>
          <p className="text-sm text-gray-700">{game.description}</p>
        </div>
      )}

      {/* Slot Block Status & Toggle */}
      {game.court_id && game.status !== 'converted' && (
        <div className={`border rounded-xl p-4 mb-6 ${
          game.slot_blocked 
            ? 'bg-green-50 border-green-200' 
            : 'bg-amber-50 border-amber-200'
        }`} data-testid="slot-block-status">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              {game.slot_blocked 
                ? <Lock className="h-4 w-4 text-green-600" />
                : <Unlock className="h-4 w-4 text-amber-600" />
              }
              <div>
                <p className={`text-sm font-medium ${game.slot_blocked ? 'text-green-800' : 'text-amber-800'}`}>
                  Calendar Slot: {game.slot_blocked ? 'Blocked' : 'Not Blocked'}
                </p>
                <p className={`text-xs ${game.slot_blocked ? 'text-green-600' : 'text-amber-600'}`}>
                  {game.slot_blocked 
                    ? 'Regular bookings are prevented on this slot' 
                    : 'This slot is open for regular bookings'
                  }
                </p>
              </div>
            </div>
            {isActive && (
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  className={`gap-1.5 ${game.slot_blocked ? 'text-amber-700 border-amber-300 hover:bg-amber-100' : 'text-green-700 border-green-300 hover:bg-green-100'}`}
                  onClick={handleToggleSlotBlock}
                  disabled={blockingSlot}
                  data-testid="toggle-slot-block-btn"
                >
                  {blockingSlot ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 
                    game.slot_blocked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />
                  }
                  {game.slot_blocked ? 'Unblock' : 'Block'}
                </Button>
                <Button
                  size="sm"
                  className="gap-1.5 bg-blue-600 hover:bg-blue-700"
                  onClick={handleBookSlot}
                  data-testid="book-slot-btn"
                >
                  <CalendarIcon className="h-3.5 w-3.5" />
                  Book this Slot
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full Booking Dialog (admin "Book this Slot") */}
      {showBookSlot && facilityForBook && (
        <ErrorBoundary label="BookingDialog (CommunityGames)">
          <BookingDialog
            isOpen={showBookSlot}
            onClose={() => setShowBookSlot(false)}
            slotInfo={{
              date: game.date,
              time: game.start_time,
              section: game.court_id,
              status: 'available',
              duration: getGameDurationSlots(),
              community_game_id: gameId,
            }}
            facility={facilityForBook}
            onBookingCreated={handleBookingCreated}
          />
        </ErrorBoundary>
      )}

      {/* Cancel & Refund Dialog */}
      {showCancelDialog && (() => {
        const paidPlayers = (game.participants || []).filter(
          p => p.payment_status === 'paid' && p.payment_id
        );
        const totalCollected = paidPlayers.reduce(
          (s, p) => s + (Number(p.amount_paid) || 0), 0
        );
        const resolvedPct = cancelPctChoice === 'custom'
          ? Math.min(100, Math.max(0, parseInt(cancelCustomPct, 10) || 0))
          : parseInt(cancelPctChoice, 10);
        const estimatedRefund = Math.round(totalCollected * resolvedPct / 100 * 100) / 100;
        const hasLinked = !!(game.linked_booking_id || game.converted_booking_id);
        const isCompleted = game.status === 'completed';
        return (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            data-testid="cancel-dialog-overlay"
            onClick={() => !cancelling && setShowCancelDialog(false)}
          >
            <div
              className="bg-white rounded-2xl max-w-md w-full p-5 space-y-4 shadow-xl"
              data-testid="cancel-dialog"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {isCompleted ? 'Cancel Completed Game' : 'Cancel Game'}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {isCompleted
                      ? 'This game is already marked completed. Confirm the refund policy before proceeding.'
                      : 'Choose how much to refund each paid participant.'}
                  </p>
                </div>
                <button
                  onClick={() => setShowCancelDialog(false)}
                  disabled={cancelling}
                  className="text-gray-400 hover:text-gray-600"
                  data-testid="cancel-dialog-close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {paidPlayers.length > 0 ? (
                <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-700">
                  <div className="flex justify-between">
                    <span>Paid participants</span>
                    <span className="font-medium">{paidPlayers.length}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span>Total collected</span>
                    <span className="font-medium">₹{totalCollected.toLocaleString()}</span>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  No online-paid participants on this game — no participant refunds will be issued.
                </div>
              )}

              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-700">Refund % to each paid participant</label>
                <div className="grid grid-cols-4 gap-2">
                  {['100', '50', '0', 'custom'].map(v => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setCancelPctChoice(v)}
                      disabled={cancelling}
                      className={`px-2 py-2 rounded-lg text-xs font-medium border transition-colors ${
                        cancelPctChoice === v
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                      }`}
                      data-testid={`cancel-pct-${v}`}
                    >
                      {v === 'custom' ? 'Custom' : `${v}%`}
                    </button>
                  ))}
                </div>
                {cancelPctChoice === 'custom' && (
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={cancelCustomPct}
                    onChange={(e) => setCancelCustomPct(e.target.value.replace(/\D/g, '').slice(0, 3))}
                    placeholder="Enter 0-100"
                    disabled={cancelling}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    data-testid="cancel-pct-custom-input"
                  />
                )}
              </div>

              {paidPlayers.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                  <div className="flex justify-between">
                    <span>Estimated refund (total)</span>
                    <span className="font-semibold" data-testid="cancel-estimated-refund">
                      ₹{estimatedRefund.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {hasLinked && (
                <label className="flex items-start gap-2 cursor-pointer bg-gray-50 rounded-lg p-3">
                  <input
                    type="checkbox"
                    checked={cancelLinkedBooking}
                    onChange={(e) => setCancelLinkedBooking(e.target.checked)}
                    disabled={cancelling}
                    className="mt-0.5"
                    data-testid="cancel-linked-booking-toggle"
                  />
                  <div className="text-xs">
                    <div className="font-medium text-gray-800">Also cancel the linked court booking</div>
                    <div className="text-gray-500 mt-0.5">
                      Cancels booking <span className="font-mono">{game.linked_booking_id || game.converted_booking_id}</span>. The slot is released and the booking is marked cancelled; participants are refunded individually above, so no additional money moves on the booking record.
                    </div>
                  </div>
                </label>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowCancelDialog(false)}
                  disabled={cancelling}
                  data-testid="cancel-dialog-cancel-btn"
                >
                  Keep Game
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  onClick={confirmCancel}
                  disabled={cancelling}
                  data-testid="cancel-dialog-confirm-btn"
                >
                  {cancelling ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <XCircle className="h-4 w-4 mr-1" />}
                  Confirm Cancel
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Actions */}
      {isActive && (
        <div className="flex gap-3 mb-3">
          <Button 
            variant="outline" 
            className="flex-1 gap-2"
            onClick={editing ? () => setEditing(false) : handleStartEdit}
            data-testid="edit-game-btn"
          >
            {editing ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
            {editing ? 'Close Edit' : 'Edit Game'}
          </Button>
          <Button 
            variant="outline" 
            className="flex-1 gap-2"
            onClick={() => setShowAddParticipant(!showAddParticipant)}
            disabled={game.players_filled >= game.max_players}
            data-testid="add-participant-toggle"
          >
            {showAddParticipant ? <X className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
            {showAddParticipant ? 'Close' : 'Add Player'}
          </Button>
        </div>
      )}
      <div className="flex gap-3 mb-3">
        <Button variant="outline" className="flex-1 gap-2" onClick={handleCopyLink} data-testid="copy-link-btn">
          <Copy className="h-4 w-4" /> Copy Invite Link
        </Button>
        <Button
          variant="outline"
          className="flex-1 gap-2"
          onClick={handleExportGame}
          disabled={exporting}
          data-testid="export-game-btn"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export to Excel
        </Button>
        {canCancel && (
          <Button 
            variant="outline" 
            className="flex-1 gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            onClick={handleCancel}
            disabled={cancelling}
            data-testid="cancel-game-btn"
          >
            {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
            {game.status === 'completed' ? 'Cancel & Refund' : 'Cancel Game'}
          </Button>
        )}
      </div>
      <a
        href={(() => {
          const gameUrl = `${window.location.origin}${game.public_link}`;
          const text = `Join the *${game.sport}${game.format ? ` ${game.format}` : ''}* game at *${game.venue_name}*!\n\n` +
            `Date: ${formatDate(game.date)}\n` +
            `Time: ${game.start_time} - ${game.end_time}\n` +
            `Cost: ₹${game.cost_per_player}/player\n` +
            `Spots: ${Math.max(0, game.max_players - game.players_filled)} left\n\n` +
            `Register & pay here: ${gameUrl}`;
          return `https://wa.me/?text=${encodeURIComponent(text)}`;
        })()}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-medium py-2.5 rounded-lg mb-6 transition-colors text-sm"
        data-testid="admin-share-whatsapp"
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        Share on WhatsApp
      </a>

      {/* Participants List */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-sm text-gray-900">
            Participants ({confirmedParticipants.length} confirmed{waitlistParticipants.length > 0 ? `, ${waitlistParticipants.length} waitlisted` : ''})
          </h2>
        </div>

        {/* Add Participant Form */}
        {showAddParticipant && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-3 space-y-3" data-testid="add-participant-form">
            <h4 className="text-xs font-semibold text-blue-800">Add Participant (offline registration)</h4>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                value={newParticipant.name}
                onChange={e => setNewParticipant(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Name"
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                data-testid="manual-participant-name"
              />
              <input
                type="tel"
                value={newParticipant.phone}
                onChange={e => setNewParticipant(prev => ({ ...prev, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                placeholder="Phone number"
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                data-testid="manual-participant-phone"
              />
            </div>
            <Button 
              onClick={handleAddParticipant} 
              disabled={addingParticipant} 
              size="sm" 
              className="w-full gap-2"
              data-testid="add-participant-submit"
            >
              {addingParticipant ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
              Add to Game
            </Button>
          </div>
        )}

        {confirmedParticipants.length === 0 && waitlistParticipants.length === 0 && otherParticipants.length === 0 ? (
          <div className="bg-gray-50 rounded-xl p-6 text-center">
            <Users className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No participants yet</p>
            <p className="text-xs text-gray-400 mt-1">Share the invite link to get players</p>
          </div>
        ) : (
          <>
            {/* Confirmed Players */}
            {confirmedParticipants.length > 0 && (
              <div className="space-y-2 mb-4" data-testid="confirmed-participants-list">
                {confirmedParticipants.map((p, i) => (
                  <div key={p.id || i} className="bg-white border rounded-xl p-3 flex items-center justify-between" data-testid={`participant-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium">{p.name}</p>
                        {p.added_by && <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Added by admin</span>}
                        {p.waitlist_status === 'promoted' && (
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">PROMOTED</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                        <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{p.phone}</span>
                        <span>{new Date(p.joined_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${getPaymentBadge(p.payment_status)}`}>
                        {p.payment_status}
                      </span>
                      {isActive && canRemoveParticipant(p) && (
                        <button
                          onClick={() => handleRemoveParticipant(p)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          title="Remove from game (offline / unpaid only)"
                          data-testid={`remove-participant-${i}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Waitlist Section */}
            {waitlistParticipants.length > 0 && (
              <div data-testid="waitlist-section">
                <div className="flex items-center gap-2 mb-2 mt-2">
                  <h3 className="font-semibold text-xs text-amber-700 uppercase tracking-wide">Waitlist ({waitlistParticipants.length})</h3>
                  <div className="flex-1 h-px bg-amber-100" />
                </div>
                <div className="space-y-2">
                  {waitlistParticipants.map((p, i) => (
                    <div key={p.id || `wl-${i}`} className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between" data-testid={`waitlist-participant-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded font-bold tracking-wide">
                            WAITLIST #{p.waitlist_position || '?'}
                          </span>
                          <p className="text-sm font-medium">{p.name}</p>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${getPaymentBadge(p.payment_status)}`}>
                            {p.payment_status}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                          <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{p.phone}</span>
                          {p.waitlist_paid_at && (
                            <span>· paid {new Date(p.waitlist_paid_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                          )}
                        </div>
                      </div>
                      {isActive && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="ml-2 border-amber-400 text-amber-800 hover:bg-amber-100 text-xs h-8"
                          onClick={() => handlePromoteWaitlist(p.id, p.name)}
                          data-testid={`promote-waitlist-btn-${i}`}
                        >
                          Promote
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Other (expired/cancelled/refunded waitlist or refunded confirmed) */}
            {otherParticipants.length > 0 && (
              <details className="mt-4">
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 flex items-center justify-between flex-wrap gap-2">
                  <span>Show {otherParticipants.length} past/cancelled participant{otherParticipants.length > 1 ? 's' : ''}</span>
                  {isActive && otherParticipants.some(p => p.payment_status === 'pending' && p.razorpay_order_id) && (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleBulkSync(); }}
                      disabled={bulkSyncing}
                      className="text-xs font-semibold text-blue-700 hover:bg-blue-50 px-2 py-1 rounded inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid="bulk-sync-pending-btn"
                    >
                      {bulkSyncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                      Sync All Pending
                    </button>
                  )}
                </summary>
                <div className="space-y-2 mt-2">
                  {otherParticipants.map((p, i) => (
                    <div key={p.id || `other-${i}`} className="bg-gray-50 border rounded-xl p-3 flex items-center justify-between text-gray-500" data-testid={`other-participant-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm">{p.name}</p>
                          {p.waitlist_status && (
                            <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded uppercase">{p.waitlist_status.replace('_', ' ')}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs mt-0.5">
                          <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{p.phone}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${getPaymentBadge(p.payment_status)}`}>
                          {p.payment_status}
                        </span>
                        {isActive && p.payment_status === 'pending' && p.razorpay_order_id && (
                          <button
                            onClick={() => handleSyncPayment(p)}
                            disabled={syncingId === p.id}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50"
                            title="Sync payment status from Razorpay"
                            data-testid={`sync-payment-other-${i}`}
                          >
                            {syncingId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                          </button>
                        )}
                        {isActive && canRemoveParticipant(p) && (
                          <button
                            onClick={() => handleRemoveParticipant(p)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                            title="Remove from game"
                            data-testid={`remove-other-participant-${i}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============== REPORTS VIEW ==============
const ReportsView = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [stuckRefunds, setStuckRefunds] = useState(null);
  const today = new Date();
  const formatDateInput = (d) => d.toISOString().split('T')[0];
  const lastMonth = new Date(today); lastMonth.setDate(today.getDate() - 30);
  const [startDate, setStartDate] = useState(formatDateInput(lastMonth));
  const [endDate, setEndDate] = useState(formatDateInput(today));

  const presets = [
    { label: '7d', days: 7 },
    { label: '30d', days: 30 },
    { label: '90d', days: 90 },
    { label: 'All', days: null },
  ];

  const applyPreset = (days) => {
    const e = new Date();
    setEndDate(formatDateInput(e));
    if (days === null) {
      setStartDate('2024-01-01');
    } else {
      const s = new Date(); s.setDate(e.getDate() - days);
      setStartDate(formatDateInput(s));
    }
  };

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const data = await api.request(`/community-games/reports/summary?${params.toString()}`);
      setReport(data);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { loadReport(); }, [loadReport]);

  // Stuck refunds is venue-scoped on the server, no date filter; load once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.request('/community-games/reports/stuck-refunds');
        if (!cancelled) setStuckRefunds(data);
      } catch (err) {
        if (!cancelled) setStuckRefunds({ count: 0, items: [], error: err.message });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const exportCSV = () => {
    if (!report?.top_customers?.length) return;
    const rows = [['Name', 'Phone', 'Games Played', 'Total Paid (INR)']];
    report.top_customers.forEach(c => rows.push([c.name, c.phone, c.games_played, c.total_paid.toFixed(0)]));
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `community-games-report-${startDate}_to_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 lg:p-6 pb-28 lg:pb-6 max-w-4xl mx-auto" data-testid="community-games-reports">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="reports-back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold">Community Games Reports</h1>
          <p className="text-sm text-gray-500">Aggregated metrics for your venue</p>
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={exportCSV} disabled={!report?.top_customers?.length} data-testid="export-csv-btn">
          <Download className="h-4 w-4" /> CSV
        </Button>
      </div>

      {/* Date Range */}
      <div className="bg-white border rounded-xl p-4 mb-4" data-testid="reports-date-filter">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">From</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              data-testid="reports-start-date"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">To</label>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              data-testid="reports-end-date"
            />
          </div>
          <div className="flex gap-1.5 ml-auto">
            {presets.map(p => (
              <button
                key={p.label}
                onClick={() => applyPreset(p.days)}
                className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg hover:bg-gray-200"
                data-testid={`preset-${p.label}`}
              >{p.label}</button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : !report ? (
        <p className="text-sm text-gray-500">No data available.</p>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-white border rounded-xl p-4" data-testid="kpi-total-games">
              <Trophy className="h-4 w-4 text-amber-500 mb-2" />
              <p className="text-2xl font-bold">{report.total_games}</p>
              <p className="text-xs text-gray-500">Total Games</p>
            </div>
            <div className="bg-white border rounded-xl p-4" data-testid="kpi-total-players">
              <Users className="h-4 w-4 text-purple-500 mb-2" />
              <p className="text-2xl font-bold">{report.total_players}</p>
              <p className="text-xs text-gray-500">Total Players ({report.unique_players} unique)</p>
            </div>
            <div className="bg-white border rounded-xl p-4" data-testid="kpi-revenue">
              <IndianRupee className="h-4 w-4 text-green-500 mb-2" />
              <p className="text-2xl font-bold">&#8377;{(report.total_revenue || 0).toLocaleString('en-IN')}</p>
              <p className="text-xs text-gray-500">Net Revenue</p>
            </div>
            <div className="bg-white border rounded-xl p-4" data-testid="kpi-refunded">
              <XCircle className="h-4 w-4 text-red-500 mb-2" />
              <p className="text-2xl font-bold">&#8377;{(report.total_refunded || 0).toLocaleString('en-IN')}</p>
              <p className="text-xs text-gray-500">Refunded</p>
            </div>
          </div>

          {/* Breakdown by format */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            <div className="bg-white border rounded-xl p-4">
              <h3 className="text-sm font-semibold mb-3">By Format</h3>
              {Object.keys(report.by_format || {}).length === 0 ? (
                <p className="text-xs text-gray-400">No games yet</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(report.by_format).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm" data-testid={`format-row-${k}`}>
                      <span className="text-gray-600 capitalize">{k.replace('-', ' ')}</span>
                      <span className="font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white border rounded-xl p-4">
              <h3 className="text-sm font-semibold mb-3">By Status</h3>
              {Object.keys(report.by_status || {}).length === 0 ? (
                <p className="text-xs text-gray-400">No games yet</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(report.by_status).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm" data-testid={`status-row-${k}`}>
                      <span className="text-gray-600 capitalize">{k}</span>
                      <span className="font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Top Customers */}
          <div className="bg-white border rounded-xl p-4 mb-6" data-testid="top-customers-section">
            <h3 className="text-sm font-semibold mb-3">Top Customers (by games played)</h3>
            {report.top_customers?.length === 0 ? (
              <p className="text-xs text-gray-400">No customers yet</p>
            ) : (
              <div className="space-y-2">
                {report.top_customers.slice(0, 20).map((c, i) => (
                  <div key={c.phone || i} className="flex justify-between items-center py-2 border-b last:border-0 text-sm" data-testid={`top-customer-${i}`}>
                    <div>
                      <p className="font-medium">{c.name}</p>
                      <p className="text-xs text-gray-500">{c.phone}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{c.games_played} games</p>
                      <p className="text-xs text-gray-500">&#8377;{c.total_paid.toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Stuck Refunds — visibility-only operator widget */}
          <StuckRefundsWidget data={stuckRefunds} />
        </>
      )}
    </div>
  );
};

const StuckRefundsWidget = ({ data }) => {
  const [items, setItems] = useState(data?.items || []);
  const [confirmingId, setConfirmingId] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);

  // Sync incoming data into local state (initial load + refetch).
  useEffect(() => {
    setItems(data?.items || []);
  }, [data]);

  const formatTs = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
      });
    } catch {
      return iso;
    }
  };

  const rowKey = (it) => it.kind === 'event_registration'
    ? `event__${it.registration_id}`
    : it.kind === 'coaching_registration'
    ? `coaching__${it.registration_id}`
    : `${it.game_id}__${it.participant_id}`;

  const handleResolve = async (it) => {
    const key = rowKey(it);
    setResolvingId(key);
    try {
      if (it.kind === 'event_registration') {
        await api.request(
          `/events/admin/stuck-refunds/${it.registration_id}/reconcile`,
          { method: 'POST' }
        );
      } else if (it.kind === 'coaching_registration') {
        await api.request(
          `/coaching/admin/stuck-refunds/${it.registration_id}/reconcile`,
          { method: 'POST' }
        );
      } else {
        await api.request(
          `/community-games/${it.game_id}/participants/${it.participant_id}/resolve-refund`,
          { method: 'POST' }
        );
      }
      setItems(prev => prev.filter(x => rowKey(x) !== key));
      setConfirmingId(null);
      toast({ title: 'Marked resolved', description: `${it.participant_name || 'Refund'} cleared from queue.` });
    } catch (err) {
      toast({ title: 'Could not resolve', description: err.message, variant: 'destructive' });
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div
      className="bg-white border border-amber-200 rounded-xl p-4 mb-6"
      data-testid="stuck-refunds-section"
    >
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <h3 className="text-sm font-semibold">Stuck Refunds</h3>
        {data && (
          <span
            className="ml-auto text-xs font-medium bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full"
            data-testid="stuck-refunds-count"
          >
            {items.length}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Participants where a refund was started but no Razorpay refund ID was recorded.
        Reconcile manually before marking resolved.
      </p>

      {!data ? (
        <div className="flex items-center gap-2 py-2 text-xs text-gray-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : data.error ? (
        <p className="text-xs text-red-500" data-testid="stuck-refunds-error">
          Failed to load: {data.error}
        </p>
      ) : items.length === 0 ? (
        <p className="text-xs text-gray-400" data-testid="stuck-refunds-empty">
          No stuck refunds — everything is clean.
        </p>
      ) : (
        <div className="divide-y">
          {items.map((it, i) => {
            const key = rowKey(it);
            const isConfirming = confirmingId === key;
            const isResolving = resolvingId === key;
            const isEvent = it.kind === 'event_registration';
            const gameLabel = isEvent
              ? `EVENT · ${it.event_title || ''}${it.activity_title ? ' / ' + it.activity_title : ''}`
              : [it.venue_name, it.sport, it.format].filter(Boolean).join(' • ');
            const when = [it.game_date, it.start_time].filter(Boolean).join(' ');
            return (
              <div
                key={key}
                className="py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm"
                data-testid={`stuck-refund-row-${i}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate" data-testid={`stuck-refund-participant-${i}`}>
                    {it.participant_name || 'Unknown player'}
                  </p>
                  <p className="text-xs text-gray-500 truncate" data-testid={`stuck-refund-game-${i}`}>
                    {gameLabel || 'Game'}{when ? ` — ${when}` : ''}
                  </p>
                </div>
                <div className="text-left sm:text-right shrink-0">
                  <p className="font-semibold" data-testid={`stuck-refund-amount-${i}`}>
                    &#8377;{(it.amount_paid || 0).toLocaleString('en-IN')}
                  </p>
                  <p className="text-xs text-gray-500" data-testid={`stuck-refund-time-${i}`}>
                    Initiated {formatTs(it.refund_initiated_at)}
                  </p>
                </div>
                <div className="shrink-0 sm:ml-3">
                  {isConfirming ? (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-500 mr-1">Confirm?</span>
                      <button
                        onClick={() => handleResolve(it)}
                        disabled={isResolving}
                        className="px-2.5 py-1 text-xs font-medium rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-60 inline-flex items-center gap-1"
                        data-testid={`stuck-refund-confirm-${i}`}
                      >
                        {isResolving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                        Yes
                      </button>
                      <button
                        onClick={() => setConfirmingId(null)}
                        disabled={isResolving}
                        className="px-2.5 py-1 text-xs rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
                        data-testid={`stuck-refund-cancel-${i}`}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmingId(key)}
                      className="px-2.5 py-1 text-xs font-medium rounded-md border border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid={`stuck-refund-resolve-${i}`}
                    >
                      Mark Resolved
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============== MAIN COMPONENT ==============
const BulkExportModal = ({ currentUser, facilities, onClose }) => {
  const isSuper = currentUser?.role === 'superadmin';
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = (() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  })();
  const [dateFrom, setDateFrom] = useState(monthAgo);
  const [dateTo, setDateTo] = useState(today);
  const [venueId, setVenueId] = useState('');
  const [sport, setSport] = useState('');
  const [status, setStatus] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState('');

  const rangeDays = (() => {
    if (!dateFrom || !dateTo) return 0;
    const a = new Date(dateFrom);
    const b = new Date(dateTo);
    return Math.round((b - a) / (1000 * 60 * 60 * 24));
  })();
  const rangeInvalid = rangeDays > 90 || rangeDays < 0;

  const doExport = async () => {
    setErr('');
    if (!dateFrom || !dateTo) { setErr('Both dates are required'); return; }
    if (rangeDays < 0) { setErr('"To" date must be on/after "From" date'); return; }
    if (rangeDays > 90) { setErr('Maximum date range is 90 days'); return; }
    setDownloading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (isSuper && venueId) params.set('venue_id', venueId);
      if (sport) params.set('sport', sport);
      if (status) params.set('status', status);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/community-games/export/bulk?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Export failed');
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const fileName = m ? m[1] : `CommunityGames_${dateFrom}_to_${dateTo}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Downloaded', description: fileName });
      onClose();
    } catch (e) {
      setErr(e.message || 'Export failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4" data-testid="bulk-export-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Export Community Games</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
        </div>

        {err && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 mb-3 text-xs text-red-700" data-testid="bulk-export-error">{err}</div>
        )}

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Date From *</label>
              <input type="date" value={dateFrom} max={today}
                onChange={e => setDateFrom(e.target.value)}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                data-testid="bulk-export-date-from" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Date To *</label>
              <input type="date" value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                data-testid="bulk-export-date-to" />
            </div>
          </div>

          {dateFrom && dateTo && (
            <p className={`text-[11px] ${rangeInvalid ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
              {rangeDays} day{rangeDays === 1 ? '' : 's'} selected{rangeInvalid ? ' — maximum is 90' : ''}
            </p>
          )}

          {isSuper && (
            <div>
              <label className="text-xs font-semibold text-gray-700">Venue</label>
              <select value={venueId} onChange={e => setVenueId(e.target.value)}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white"
                data-testid="bulk-export-venue">
                <option value="">All venues</option>
                {facilities.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Sport (optional)</label>
              <select value={sport} onChange={e => setSport(e.target.value)}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white capitalize"
                data-testid="bulk-export-sport">
                <option value="">All sports</option>
                {['football', 'cricket', 'tennis', 'badminton', 'basketball', 'pickleball', 'box_cricket'].map(s => (
                  <option key={s} value={s} className="capitalize">{s.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Status (optional)</label>
              <select value={status} onChange={e => setStatus(e.target.value)}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white capitalize"
                data-testid="bulk-export-status">
                <option value="">All statuses</option>
                {['upcoming', 'open', 'full', 'completed', 'cancelled', 'expired'].map(s => (
                  <option key={s} value={s} className="capitalize">{s}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={doExport} disabled={downloading || rangeInvalid}
            className="flex-1" data-testid="bulk-export-submit">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
            Export
          </Button>
        </div>
      </div>
    </div>
  );
};

const CommunityGames = () => {
  const { user, facility } = useAuth();
  const [view, setView] = useState('list'); // list | create | detail
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [facilities, setFacilities] = useState([]);

  const loadGames = useCallback(async () => {
    setLoading(true);
    try {
      let endpoint = '/community-games';
      if (statusFilter && statusFilter !== 'all') {
        endpoint += `?status_filter=${statusFilter}`;
      }
      const data = await api.request(endpoint);
      setGames(data);
    } catch (err) {
      console.error('Failed to load games:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadGames(); }, [loadGames]);

  // Superadmin needs the venue list for the bulk export filter
  useEffect(() => {
    if (user?.role !== 'superadmin') return;
    (async () => {
      try {
        const data = await api.request('/facilities');
        setFacilities(data || []);
      } catch (e) { /* non-blocking */ }
    })();
  }, [user?.role]);

  if (view === 'create') {
    return (
      <CreateGameForm
        facility={facility}
        onBack={() => setView('list')}
        onCreated={(game) => {
          setView('detail');
          setSelectedGameId(game.id);
          loadGames();
        }}
      />
    );
  }

  if (view === 'detail' && selectedGameId) {
    return (
      <GameDetail
        gameId={selectedGameId}
        onBack={() => { setView('list'); loadGames(); }}
      />
    );
  }

  if (view === 'reports') {
    return <ReportsView onBack={() => setView('list')} />;
  }

  return (
    <GameListing
      games={games}
      loading={loading}
      onCreateNew={() => setView('create')}
      onViewGame={(id) => { setSelectedGameId(id); setView('detail'); }}
      onViewReports={() => setView('reports')}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      currentUser={user}
      facilities={facilities}
    />
  );
};

export default CommunityGames;
